/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import {declareEntity} from '../fw/MetaDataHelper.ts';
import {FLOW_STEP_TYPES, FLOW_STEP_HOOK, FLOW_STEP_INCOMING_MAIL, FETCH_DURABILITY, FETCH_DURABILITY_NORMAL} from './FlowStepDefType';

export const FLOW_MAX_ATTEMPTS = 10;

export const INVOCATION_PRIORITY_VERY_HIGH = "very-high";
export const INVOCATION_PRIORITY_HIGH = "high";
export const INVOCATION_PRIORITY_NORMAL = "normal";
export const INVOCATION_PRIORITY_LOW = "low";
export const INVOCATION_PRIORITY_VERY_LOW = "very-low";
export const INVOCATION_PRIORITY = {
    [INVOCATION_PRIORITY_VERY_HIGH]: {
        name: "Very High priority",
        priority: 1,
    },
    [INVOCATION_PRIORITY_HIGH]: {
        name: "Higher priority",
        priority: 10,
    },
    [INVOCATION_PRIORITY_NORMAL]: {
        name: "Normal priority",
        priority: 20,
    },
    [INVOCATION_PRIORITY_LOW]: {
        name: "Lower priority",
        priority: 30,
    },
    [INVOCATION_PRIORITY_VERY_LOW]: {
        name: "Very low priority",
        priority: 40,
    },
};

export const CRON_CONSTANTS = {
    '@nightly': {
        name: 'Once during night',
        cron: ['0 0 * * *'],
        distribution: 6 * 60 * 60, // seconds
    },
    '@earlyNight': {
        name: 'Once during night from 22:00 to 2:00',
        cron: ['0 22 * * *'],
        distribution: 4 * 60 * 60, // seconds
    },
    '@lateNight': {
        name: 'Once during night from 1:00 to 5:00',
        cron: ['0 1 * * *'],
        distribution: 4 * 60 * 60, // seconds
    },
    '@lateNight2': {
        name: 'Once during night from 3:00 to 5:00',
        cron: ['0 3 * * *'],
        distribution: 2 * 60 * 60, // seconds
    },
    '@hourlyWeekDay': {
        name: 'Hourly during weekday, less often other times',
        cron: [
            '0 6-20 * * *',
            '0 3,22 * * *',
        ],
        distribution: 1 * 60 * 60, // seconds
    },
    '@hourlyWeekDayOnly': {
        name: 'Hourly during weekday only (6-20 only)',
        cron: [
            '0 6-20 * * *',
        ],
        distribution: 1 * 60 * 60, // seconds
    },
    '@hourly': {
        name: 'Hourly',
        cron: ['0 * * * *'],
        distribution: 1 * 60 * 60, // seconds
    },
    '@30minutesWeekDay': {
        name: 'Every 30 minutes during weekday, less often other times',
        cron: [
            '*/30 8-17 * * *',
            '0 6-8,17-21 * * *',
            '0 3,22 * * *',
        ],
        distribution: 30 * 60, // seconds
    },
    '@15minutesWeekDay': {
        name: 'Every 15 minutes during weekday, less often other times',
        cron: [
            '*/15 8-17 * * *',
            '*/30 6-8,17-21 * * *',
            '0 21-23,0-6 * * *',
        ],
        distribution: 15 * 60, // seconds
    },
    '@15minutes': {
        name: 'Every 15 minutes every day',
        cron: [
            '*/15 * * * *',
        ],
        distribution: 15 * 60, // seconds
    },
    '@5minutesWeekDay': {
        name: 'Every 5 minutes during weekday, less often other times',
        cron: [
            '*/5 8-17 * * *',
            '*/30 6-8,17-21 * * *',
            '0 21-23,0-6 * * *',
        ],
        distribution: 5 * 60, // seconds
    },
    '@onceWorkingHours': {
        name: 'Once a day during working hours',
        cron: ['0 10 * * *'],
        distribution: 5 * 60 * 60, // seconds
    },
    '@4timesADay': {
        name: '4x times a day',
        cron: ['0 7,12,16,22 * * *'],
        distribution: 3 * 60 * 60, // seconds
    },
    '@monthly': {
        name: "Every month once",
        cron: ['0 0 1 * *'],
        distribution: 8 * 60 * 60, // seconds
    },
};

export default declareEntity({
    entity: "flowInvocations",
    graphQL: "FlowInvocation",
    name: "Flow Invocations",
    dynamoPrefix: 'org',
    storageType: 'silo',
    parentRelation: "invocations",
	defaultOrder: [['createdAt', 'desc'], 'id'],
    defaultTitle: (data) => {
        return data['createdAt'];
    },
    userEditable: false,
    optimizeReading: true,
    fields: (types) => { return {
        organisation: {
            name: "Organisation ID",
            type: "relation",
            to: () => types['organisations'],
            defaultValue: (flow) => {
                return flow['organisation_id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
        },
        instance: {
            name: "Instance",
            type: "relation",
            to: () => types['instances'],
            userEditable: false,
            hiddenCreate: true,
            defaultValue: (parent) => {
                if (parent) {
                    return parent['instance_id'];
                }
            },
        },
        id: {
            name: "My Flow Invocation ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            compositeKey: true,
        },
        priority: {
            name: "Priority",
            type: "enum",
            defaultValue: INVOCATION_PRIORITY_NORMAL,
            values: INVOCATION_PRIORITY,
        },
        fetchDurability: {
            name: "Fetch Durability",
            type: "enum",
            defaultValue: FETCH_DURABILITY_NORMAL,
            values: FETCH_DURABILITY,
        },
        status: {
            name: "Status",
            type: "object",
            offload: true,
            defaultValue: {},
        },
        maxProgress: {
            name: "Max Progress",
            type: "integer",
        },
        currentProgress: {
            name: "Progress",
            type: "integer",
        },
        startType: {
            name: 'Start type',
            type: 'enum',
            filterable: true,
            values: FLOW_STEP_TYPES,
        },
        flow: {
            name: "Flow",
            to: () => types['flows'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        flowDef: {
            name: "Flow Definition",
            to: () => types['flowDefs'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['flowDef_id'];
            },
            required: true,
            hiddenCreate: true,
        },
        flowDefVersion: {
            name: "Flow Definition Version",
            to: () => types['flowDefVersions'],
            defaultValue: (flow) => {
                return flow['flowDefVersion_id'];
            },
            type: "relation",
            hiddenCreate: true,
        },
        flowStepFlowDef: {
            name: "Flow Definition for Flow Step", // starting step
            type: "relation",
            to: () => types['flowDefs'],
        },
        flowStepDef: {
            name: "Flow Step Definition", // starting step
            type: "relation",
            to: () => types['flowStepDefs'],
            relativeTo: ["flowStepFlowDef"],
        },
        failedFlowStepDef: {
            name: "Failed Flow Step", // failing step
            type: "relation",
            to: () => types['flowStepDefs'],
            relativeTo: ["flowDef"],
        },
        flowIncomingEmail: {
            name: "Flow Incoming Email",
            type: "relation",
            to: () => types['flowIncomingEmails'],
        },
        failed: {
            name: "Failed",
            type: "boolean",
            filterable: true,
            defaultValue: false,
        },
        tryCancel: {
            name: "Request for stop",
            type: "boolean",
            defaultValue: false,
        },
        cancelled: {
            name: "Stopped",
            type: "boolean",
            defaultValue: false,
        },
        started: {
            name: "Started",
            type: "boolean",
            defaultValue: false,
        },
        finished: {
            name: "Finished",
            type: "boolean",
            defaultValue: false,
        },
        nextRetry: {
            name: "Next retry at",
            type: "datetime",
        },
        attempt: {
            name: "Attempt",
            type: "int",
            defaultValue: 0,
        },
        debug: {
            name: "Debug",
            type: "boolean",
            defaultValue: false,
        },
        apiCount: {
            name: "API Calls",
            type: "int",
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getApiCount",
        },
        stats: {
            name: "Statistics",
            type: "object",
            defaultValue: {},
        },
        lastRun: {
            name: "Last run",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
        },
        duration: {
            name: "Request duration (ms)",
            type: "int",
            defaultValue: 0,
        },
        finishedAt: {
            name: "Finished At",
            type: "datetime",
        },
        expiresIn: {
            name: "Expires in",
            description: "We need to delete old flowInvocations",
            type: "int",
            userEditable: false,
            export: false,
            import: false,
            defaultValue: () => {
                /* expire after 120 days */
                return Math.floor(Date.now() / 1000) + (24 * 60 * 60) * 70;
            },
        },
        error: {
            name: "Error",
            type: "text",
        },
        originalUrl: {
            name: "Original request URL",
            type: "string",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
        },
        remoteIP: {
            name: "Remote IP",
            type: "string",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK; },
        },
        originalRequest: {
            name: "Original request content",
            type: "object",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
            offload: true,
        },
        originalRequest2: {
            name: "Original request content 2",
            type: "binary",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getStoredOriginalRequest",
        },
        originalRequest2Preview: {
            name: "Original request content 2 preview",
            type: "binary",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getStoredOriginalRequestPreview",
        },
        originalHeaders: {
            name: "Original request headers",
            type: "object",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
        },
        originalRequestUrl: {
            name: "Original request content (if large)",
            type: "string",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK || data['startType'] === FLOW_STEP_INCOMING_MAIL; },
        },
        resultStatusCode: {
            name: "Result response",
            type: "integer",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK; },
        },
        resultContentType: {
            name: "Result Content-Type",
            type: "string",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK; },
        },
        resultResponse: {
            name: "Result response",
            type: "binary",
            offload: true,
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK; },
        },
        resultResponseUrl: {
            name: "Result response URL",
            type: "string",
            visibility: (data) => { return data['startType'] === FLOW_STEP_HOOK; },
        },
        productVersion: {
            name: "Platform version",
            type: "string",
        },
        productStage: {
            name: "Stage",
            type: "string",
        },
        author: {
            name: "Created By",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            filterable: true,
            userEditable: false,
        },
        state: {
            name: "Internal State",
            type: "object",
            defaultValue: {},
            userEditable: false,
        },
    }; },
    relations: (types) => { return {
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "flow_id",
                    to: "flow_id"
                },
                {
                    from: "id",
                    to: "flowInvocation_id"
                }
            ]
        },
        logger: {
            type: 'oneToMany',
            to: types['loggers'],
            foreignKeys: [
                {
                    from: "id",
                    to: "flowInvocation_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },
    }; }
});
